/*
  Auto free gift — configured under Theme settings > Free Gift
  (window.TBFreeGift is set by snippets/free-gift.liquid).

  Adds the gift product when the cart subtotal (excluding the gift) reaches
  the threshold, removes it when the cart drops back below, and stops
  re-adding for the rest of the session if the customer removes it manually.
  The gift line is tagged with the hidden `_free_gift` property.
*/
(function () {
  const config = window.TBFreeGift;
  if (!config || !config.variantId || !config.threshold) return;

  const DISMISSED_KEY = 'tb-free-gift-dismissed';
  let hadGift = false;
  let syncing = false;

  // Any line with the gift variant is managed — including lines the customer
  // added directly from the product URL — so an unqualified $0 gift gets
  // removed. The gift must therefore be a dedicated product, not one that is
  // also sold normally.
  const isGiftLine = (item) => item.id === config.variantId;

  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const renderedSections = ['cart-drawer', 'cart-icon-bubble'];

  function updateSections(sections) {
    if (!sections) return;
    const drawer = document.querySelector('cart-drawer');
    const drawerTarget = document.querySelector('#CartDrawer');
    if (drawer && drawerTarget && sections['cart-drawer']) {
      const fresh = new DOMParser()
        .parseFromString(sections['cart-drawer'], 'text/html')
        .querySelector('#CartDrawer');
      if (fresh) {
        drawerTarget.innerHTML = fresh.innerHTML;
        const overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const fresh = new DOMParser()
        .parseFromString(sections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');
      if (fresh) bubble.innerHTML = fresh.innerHTML;
    }
  }

  async function fetchCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, { headers: jsonHeaders });
    if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
    return response.json();
  }

  async function addGift() {
    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        items: [{ id: config.variantId, quantity: 1, properties: { _free_gift: 'true' } }],
        sections: renderedSections,
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) return false; // e.g. gift sold out — leave the cart alone
    updateSections(data.sections);
    return true;
  }

  async function setGiftQuantity(line, quantity) {
    const response = await fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        id: line.key,
        quantity,
        sections: renderedSections,
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok) return;
    updateSections(data.sections);
  }

  async function sync() {
    if (syncing) return;
    syncing = true;
    try {
      const cart = await fetchCart();
      const giftLines = cart.items.filter(isGiftLine);
      const giftTotal = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
      const subtotal = cart.items_subtotal_price - giftTotal;
      const qualifies = subtotal >= config.threshold;

      if (!qualifies) {
        sessionStorage.removeItem(DISMISSED_KEY); // re-offer if they cross the threshold again
        for (const line of giftLines) await setGiftQuantity(line, 0);
        hadGift = false;
      } else if (giftLines.length === 0) {
        if (hadGift) {
          // Customer removed the gift themselves — don't fight them this session.
          sessionStorage.setItem(DISMISSED_KEY, '1');
          hadGift = false;
        } else if (sessionStorage.getItem(DISMISSED_KEY) !== '1') {
          hadGift = await addGift();
        }
      } else {
        hadGift = true;
        // Collapse to a single gift line of quantity 1 (a direct add creates a
        // separate untagged line alongside the auto-added one).
        for (let i = 1; i < giftLines.length; i++) await setGiftQuantity(giftLines[i], 0);
        if (giftLines[0].quantity !== 1) await setGiftQuantity(giftLines[0], 1);
      }
    } catch (error) {
      // Network hiccup — the next cart update will retry.
    } finally {
      syncing = false;
    }
  }

  function init() {
    sync();
    // PUB_SUB_EVENTS is a top-level `const` in constants.js — a global binding
    // but not a window property, so it must be checked via typeof.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => sync());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
